/**
 * Shared read-only datasheet cho các tab `/purchase/control`.
 * Style copy SaleContract `ContractListView`: filter bar → Divider → app-dataframe → TableVirtuoso + BrandState.
 * Load 1 lần ALL từ data source; search + pagination clientside (giống ContractListView).
 *
 * Tuân skill convert-table (§5/6/6.5/7.6/7.8/7.11/10/10.9/10.10):
 *  - Filter Bar 1 nút Search global (rowMatchesSearch) + per-column filter ▽ (useColumnFilters)
 *  - Columns visibility + reorder menu (FuturesColumnsMenu + useLocalDatasheetColumns) — cần `storageKey`
 *  - Freeze column ngang (`freeze`), Footer Total (`sum`), wrap cell (`wrap`), auto-width theo text
 *  - Conditional format cell (`cellSx`) + `redWhen` (legacy)
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { useMemo, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../components/BrandState'
import ResizeHandle from '../../components/ResizeHandle'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  type ColumnLike,
} from '../../components/dataList'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { useLocalDatasheetColumns } from '../../utils/datasheetColumns/useLocalDatasheetColumns'
import {
  HIDE_ON_MOBILE_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '../../theme/responsive'

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatInt = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : ''
}

// ID: integer thuần, KHÔNG dấu phân cách nghìn (25097 không phải 25,097).
const formatId = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = Number(value)
  return Number.isFinite(n) ? String(Math.trunc(n)) : String(value)
}

const formatNum2 = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
}

const formatNum4 = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : ''
}

const formatNum3 = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : ''
}

// Access Format "Percent": 0.05 → 5.00%
const formatPercent = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = Number(value)
  return Number.isFinite(n)
    ? `${(n * 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    : ''
}

export type DatasheetKind = 'text' | 'date' | 'datetime' | 'id' | 'int' | 'num2' | 'num3' | 'num4' | 'percent'

export interface DatasheetColumn {
  /** Key trong row object — cũng là unique id cho menu/filter/width. */
  field: string
  /** Label header hiển thị. */
  header: string
  /** Render formatter + auto-detect filter kind + auto-align (số căn phải). */
  kind: DatasheetKind
  /** Default width (px) khi user chưa resize; cũng dùng cho auto-width fallback. */
  width?: number
  /** Override căn lề cell (mặc định: số → right, còn lại → left). */
  align?: 'left' | 'right'
  /** Override auto-detect filter kind của per-column ▽ (text/checkboxes/date/number/none). */
  filter?: ColumnFilterKind
  /** Opt-in WRAP: chữ dài xuống dòng + dòng auto-cao (§7.8). */
  wrap?: boolean
  /** Freeze cột (sticky ngang khi scroll — §7.6). CHỈ 1 cột nên freeze. */
  freeze?: boolean
  /** Cột được cộng ở dòng Total footer (§10.10). KHÔNG có cột nào sum → không render footer. */
  sum?: boolean
  /** Cell ĐỎ theo điều kiện (Access ConditionalFormat / ForeColor 255). Legacy — ưu tiên `cellSx`. */
  redWhen?: (value: unknown, row: Record<string, unknown>) => boolean
  /** Conditional format tuỳ ý theo row (Access ConditionalFormat) — trả sx áp lên TableCell. */
  cellSx?: (row: Record<string, unknown>) => SxProps<Theme> | undefined
}

export const renderDatasheetCell = (value: unknown, kind: DatasheetKind): string => {
  switch (kind) {
    case 'date':
      return formatDate(value)
    case 'datetime':
      return formatDateTime(value)
    case 'id':
      return formatId(value)
    case 'int':
      return formatInt(value)
    case 'num2':
      return formatNum2(value)
    case 'num3':
      return formatNum3(value)
    case 'num4':
      return formatNum4(value)
    case 'percent':
      return formatPercent(value)
    default:
      return value == null ? '' : String(value)
  }
}

const isNumKind = (k: DatasheetKind) =>
  k === 'id' || k === 'int' || k === 'num2' || k === 'num3' || k === 'num4' || k === 'percent'

const colAlign = (c: DatasheetColumn): 'left' | 'right' => c.align ?? (isNumKind(c.kind) ? 'right' : 'left')

/** Map DatasheetKind → ColumnLike.kind mà inferColumnFilterKind hiểu (number/date/datetime/text). */
const filterColumnLike = (c: DatasheetColumn): ColumnLike => ({
  key: c.field,
  label: c.header,
  filter: c.filter,
  kind: isNumKind(c.kind) ? 'number' : c.kind === 'date' || c.kind === 'datetime' ? c.kind : 'text',
})

/** Width fallback cho cột freeze khi user chưa resize + col.width chưa set. */
const FREEZE_FALLBACK_WIDTH = 140

export type DatasheetRow = Record<string, unknown> & { id: string }

/** Shim column cho useLocalDatasheetColumns/FuturesColumnsMenu (cần key + label). */
type MenuColumn = DatasheetColumn & { key: string; label: string }

interface Props {
  columns: DatasheetColumn[]
  rows: DatasheetRow[]
  isLoading: boolean
  isFetching: boolean
  error: unknown
  /** Text the Range row uses; nếu không truyền lấy giá trị cột đầu. */
  primaryField?: string
  searchPlaceholder?: string
  errorTitle?: string
  emptyTitle?: string
  emptyDescription?: string
  /** Slot trên cùng — nằm trong filter bar (vd. inner tabs hoặc action buttons). */
  rightSlot?: ReactNode
  /** Bảng FILL hết chiều cao parent (bỏ cap 620) — CHỈ bật khi parent bounded
      (vd ProcurementPurchaseListView trong Card). Default false = giữ height cố định an toàn. */
  fillHeight?: boolean
  /** Row selection (toolbar Copy/Edit/Delete/Print cần current record — Access datasheet). */
  selectedRowId?: string | null
  /** Click row → toggle select (click lại unselect). Chỉ bật khi truyền handler. */
  onRowSelect?: (row: DatasheetRow | null) => void
  /** Cap chiều cao khi KHÔNG fillHeight (default 620). */
  maxBodyHeight?: number
  /** Key persist column visibility/order (mỗi consumer/mode 1 key riêng, vd
      'procurement.contract-detail.paydata_p'). Không truyền → ẩn nút Columns. */
  storageKey?: string
}

export default function ProcurementDatasheet({
  columns,
  rows,
  isLoading,
  isFetching,
  error,
  searchPlaceholder,
  errorTitle = 'Could not load data',
  emptyTitle = 'No data',
  emptyDescription = 'Try adjusting filters above.',
  rightSlot,
  fillHeight = false,
  selectedRowId,
  onRowSelect,
  maxBodyHeight = 620,
  storageKey,
}: Props) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: string; position: ColumnFilterAnchorPosition } | null>(null)
  const { getWidth, startResize, resetWidths } = useColumnWidths('procurement.datasheet')

  const columnsByField = useMemo(() => new Map(columns.map((c) => [c.field, c] as const)), [columns])

  // ---- Column visibility menu (useLocalDatasheetColumns dùng key/label) ----
  const menuColumns = useMemo<MenuColumn[]>(
    () => columns.map((c) => ({ ...c, key: c.field, label: c.header })),
    [columns],
  )
  const baseColumnsCtrl = useLocalDatasheetColumns<MenuColumn>({
    allColumns: menuColumns,
    storageKey: storageKey ?? 'procurement.datasheet.__unused__',
  })
  // Nút "Default" reset CẢ width (mặc định menu chỉ reset order/hidden) → về col.width.
  const columnsCtrl = useMemo(
    () => ({
      ...baseColumnsCtrl,
      resetToDefault: () => {
        baseColumnsCtrl.resetToDefault()
        resetWidths()
      },
    }),
    [baseColumnsCtrl, resetWidths],
  )
  // Không có storageKey → bỏ qua controller, hiện toàn bộ cột theo thứ tự khai báo.
  const visibleColumns: DatasheetColumn[] = storageKey ? columnsCtrl.visibleColumns : columns

  const filteredRows = useMemo<DatasheetRow[]>(
    () =>
      rows.filter(
        (row) =>
          rowMatchesSearch(row, search, columns.map((c) => c.field)) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [columns, rows, search, columnFilters.filters],
  )

  const visibleRows = useMemo<DatasheetRow[]>(() => {
    if (rowsPerPage === -1) return filteredRows
    const start = page * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])

  // ---- Per-column popover meta ----
  const popoverColumn = useMemo(
    () => (colPopover ? columnsByField.get(colPopover.key) ?? null : null),
    [colPopover, columnsByField],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(filterColumnLike(popoverColumn), rows) : 'text'),
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(rows, colPopover.key) : undefined),
    [popoverKind, colPopover, rows],
  )

  // ---- Auto width theo text (§7.11) ----
  const autoColWidths = useMemo<Record<string, number>>(() => {
    const CHAR_PX = 7.2
    const HEADER_PAD = 48 // padding + sort/filter/resize icons
    const CELL_PAD = 24
    const MIN = 84
    const MAX = 260
    const sample = filteredRows.slice(0, 400)
    const out: Record<string, number> = {}
    for (const col of visibleColumns) {
      let bodyMax = 0
      for (const r of sample) {
        const len = renderDatasheetCell(r[col.field], col.kind).length
        if (len > bodyMax) bodyMax = len
      }
      const headerPx = col.header.length * CHAR_PX + HEADER_PAD
      const bodyPx = bodyMax * CHAR_PX + CELL_PAD
      const minForType = col.kind === 'date' || col.kind === 'datetime' ? 124 : MIN
      out[col.field] = Math.round(Math.min(MAX, Math.max(minForType, headerPx, bodyPx)))
    }
    return out
  }, [visibleColumns, filteredRows])

  // Resolve width 3 tầng: user-resize → col.width → (freeze fallback | auto-theo-text).
  const resolveWidth = (col: DatasheetColumn): number => {
    const resized = getWidth(col.field)
    if (resized != null) return resized
    if (col.width != null) return col.width
    return col.freeze ? FREEZE_FALLBACK_WIDTH : autoColWidths[col.field] ?? 100
  }

  const minTableWidth = useMemo(
    () => Math.max(320, visibleColumns.reduce((acc, c) => acc + resolveWidth(c), 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleColumns, autoColWidths, getWidth],
  )

  // ---- Footer Total (§10.10) ----
  const sumColumns = useMemo(() => visibleColumns.filter((c) => c.sum), [visibleColumns])
  const sumTotals = useMemo<Record<string, number>>(() => {
    if (sumColumns.length === 0) return {}
    const out: Record<string, number> = {}
    for (const col of sumColumns) {
      let total = 0
      for (const r of filteredRows) {
        const n = Number(r[col.field])
        if (Number.isFinite(n)) total += n
      }
      out[col.field] = total
    }
    return out
  }, [sumColumns, filteredRows])
  const hasFooter = sumColumns.length > 0

  const hasSearch = Boolean(search.trim())
  const hasActiveFilters = hasSearch || columnFilters.hasAny
  const activeFilterCount = (hasSearch ? 1 : 0) + columnFilters.activeKeys.length

  const clearAll = () => {
    setSearch('')
    columnFilters.clearAll()
    setPage(0)
    searchPopover.close()
  }

  const gridKey = `${page}:${rowsPerPage}:${visibleRows.length}:${visibleColumns.length}`

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
               ...(fillHeight ? { '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' } } : {}) }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAll}
        activeFilterCount={activeFilterCount}
        rightSlot={
          rightSlot ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
              {rightSlot}
            </Stack>
          ) : undefined
        }
      >
        <FilterButton
          label="Search"
          active={hasSearch}
          onClick={(e) => searchPopover.open('search', e)}
        />
        {storageKey ? (
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e: MouseEvent<HTMLElement>) => columnsCtrl.openMenu(e.currentTarget)}
            sx={filterBarBtnSx}
          >
            Columns
          </Button>
        ) : null}
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {errorTitle}
        </Alert>
      ) : null}

      {/* Global Search popover */}
      <FilterPopover
        open={searchPopover.state !== null}
        anchorEl={searchPopover.state?.anchor ?? null}
        onClose={() => searchPopover.close()}
      >
        {searchPopover.isOpen('search') && (
          <SearchFilterContent
            value={search}
            onChange={(v) => {
              setSearch(v)
              setPage(0)
            }}
            placeholder={searchPlaceholder ?? 'Search…'}
            onClear={() => {
              setPage(0)
              searchPopover.close()
            }}
          />
        )}
      </FilterPopover>

      {/* Per-column filter popover (DUY NHẤT 1 instance) */}
      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.header ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(colPopover.key, v)
          setPage(0)
        }}
        options={popoverOptions}
      />

      {/* Columns visibility + reorder menu */}
      {storageKey ? (
        <FuturesColumnsMenu controller={columnsCtrl} />
      ) : null}

      {/* Non-fillHeight (nhiều bảng xếp chồng, vd Contract Detail Pay Info) → compact:
          bỏ height clamp 520px của .app-dataframe, để bảng co theo maxBodyHeight + scroll. */}
      <Box className={fillHeight ? 'app-dataframe' : 'app-dataframe app-dataframe--compact'}>
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            gap: { xs: 0.25, sm: 0.5 },
            flexWrap: 'wrap',
            minHeight: { xs: 22, sm: 24 },
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {rows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <Box
          sx={
            fillHeight
              ? { flex: 1, minHeight: { xs: 280, sm: 320 }, overflow: 'hidden' }
              : // Honor maxBodyHeight EXACTLY (scroll inside TableVirtuoso). KHÔNG dùng
                // tableInnerHeight — nó ép minHeight sm:320 nên bảng luôn ≥320 dù cap nhỏ hơn.
                { height: { xs: 'auto', sm: maxBodyHeight }, maxHeight: maxBodyHeight, minHeight: { xs: 200, sm: 0 }, overflow: 'hidden' }
          }
        >
          {error ? (
            <BrandState tone="error" title={errorTitle} description="Refresh the page or contact support if the issue continues." />
          ) : isLoading && rows.length === 0 ? (
            <BrandState tone="loading" title="Loading data…" />
          ) : visibleRows.length === 0 ? (
            <BrandState title={emptyTitle} description={emptyDescription} />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={visibleRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((c) => {
                    const w = resolveWidth(c)
                    const headerStyle: CSSProperties = { width: w, minWidth: w, maxWidth: w }
                    if (c.freeze) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={c.field}
                        align={colAlign(c)}
                        className="app-table-header-cell"
                        style={headerStyle}
                        sx={{ whiteSpace: 'nowrap', userSelect: 'text', ...(c.freeze ? {} : { position: 'relative' }) }}
                      >
                        {c.header}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(c.field)}
                          onClick={(position) => setColPopover({ key: c.field, position })}
                          ariaLabel={`Filter ${c.header}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(c.field, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={
                hasFooter
                  ? () => (
                      <TableRow>
                        {visibleColumns.map((c, index) => {
                          const w = resolveWidth(c)
                          const val =
                            index === 0
                              ? 'Total'
                              : c.sum
                                ? renderDatasheetCell(sumTotals[c.field] ?? 0, c.kind)
                                : ''
                          return (
                            <TableCell
                              key={c.field}
                              align={colAlign(c)}
                              className={c.freeze ? 'app-sticky-col' : undefined}
                              style={{ width: w, minWidth: w, maxWidth: w }}
                              sx={{
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                py: 0.5,
                                bgcolor: 'background.paper',
                                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {val}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  : undefined
              }
              itemContent={(_: number, row: DatasheetRow) =>
                visibleColumns.map((c) => {
                  const v = renderDatasheetCell(row[c.field], c.kind)
                  const red = c.redWhen?.(row[c.field], row) ?? false
                  const condSx = c.cellSx?.(row)
                  const w = resolveWidth(c)
                  const wrap = c.wrap || getWidth(c.field) != null
                  const baseSx: SxProps<Theme> = {
                    py: 0.5,
                    verticalAlign: 'top',
                    ...(red ? { color: 'error.main', fontWeight: 600 } : {}),
                  }
                  if (wrap) {
                    return (
                      <TableCell
                        key={c.field}
                        align={colAlign(c)}
                        className={c.freeze ? 'app-sticky-col' : undefined}
                        style={{ width: w, minWidth: w, maxWidth: w }}
                        sx={{ ...baseSx, whiteSpace: 'normal', overflowWrap: 'anywhere', ...(condSx ?? {}) }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {v || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={c.field}
                      align={colAlign(c)}
                      className={c.freeze ? 'app-sticky-col' : undefined}
                      style={{ width: w, minWidth: w, maxWidth: w }}
                      sx={{ ...baseSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...(condSx ?? {}) }}
                    >
                      <Box component="span" className="app-table-cell-text" title={v || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {v || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props) => (
                  <Table
                    {...props}
                    size="small"
                    stickyHeader
                    sx={{
                      minWidth: minTableWidth,
                      width: '100%',
                      // Mật độ gọn như bảng P-Instruction (/matching/match-instruct):
                      // ép cell nhỏ + minHeight:0 chống giãn dòng khi ít row.
                      minHeight: '0 !important',
                      // Body + footer (dòng Total) cùng fontSize để footer follow bảng dữ liệu.
                      '& .MuiTableBody-root .MuiTableCell-root, & tfoot .MuiTableCell-root': { fontSize: '0.64rem', py: 0.25, px: 0.75 },
                      '& .MuiTableHead-root .MuiTableCell-root, & .MuiTableCell-head': { fontSize: '0.66rem', py: 0.3, px: 0.5 },
                    }}
                  />
                ),
                TableHead: ((props: object) => <TableHead {...props} component="thead" />) as never,
                TableBody: ((props: object) => <TableBody {...props} component="tbody" />) as never,
                TableRow: (props) => {
                  const { item, ...rest } = props as unknown as { item?: DatasheetRow } & Record<string, unknown>
                  const isSelected = Boolean(item && selectedRowId != null && item.id === selectedRowId)
                  return (
                    <TableRow
                      {...(rest as object)}
                      hover
                      selected={isSelected}
                      onClick={
                        onRowSelect && item
                          ? () => onRowSelect(isSelected ? null : item)
                          : undefined
                      }
                      sx={{ cursor: 'pointer' }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
